import React, { useState } from 'react';
import ReplyModal from './ReplyModal';

const MessageDetailModal = ({ message, onClose, onMarkAsRead, onMarkAsReplied, onDelete, updating }) => {
    const [showReplyModal, setShowReplyModal] = useState(false);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

    if (!message) return null;

    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleString('en-IN', {
            day: '2-digit',
            month: 'long',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const handleDeleteClick = () => {
        setShowDeleteConfirm(true);
    };

    const confirmDelete = () => {
        onDelete(message.id);
        onClose();
        setShowDeleteConfirm(false);
    };

    const getStatusBadgeClass = () => {
        switch(message.status) {
            case 'unread': return 'badge-danger';
            case 'read': return 'badge-info';
            case 'replied': return 'badge-success';
            default: return 'badge-secondary';
        }
    };

    const getStatusIcon = () => {
        switch(message.status) {
            case 'unread': return 'fas fa-envelope';
            case 'read': return 'fas fa-envelope-open';
            case 'replied': return 'fas fa-reply';
            default: return 'fas fa-envelope';
        }
    };

    return (
        <>
            <div className="modal-overlay" onClick={onClose}>
                <div className="modal-content message-modal" onClick={e => e.stopPropagation()}>
                    <div className="modal-header">
                        <h2>
                            <i className="fas fa-envelope" style={{ marginRight: '10px', color: '#334f7f' }}></i>
                            Message Details
                        </h2>
                        <button className="modal-close" onClick={onClose}>
                            <i className="fas fa-times"></i>
                        </button>
                    </div>

                    <div className="modal-body">
                        {/* Customer Info Section */}
                        <div className="customer-info-section">
                            <div className="customer-avatar-large">
                                {message.name?.charAt(0).toUpperCase()}
                            </div>
                            <div className="customer-details">
                                <h3>{message.name}</h3>
                                <div className="customer-contact">
                                    <a href={`mailto:${message.email}`}>
                                        <i className="fas fa-envelope"></i>
                                        {message.email}
                                    </a>
                                    {message.phone && (
                                        <a href={`tel:${message.phone}`}>
                                            <i className="fas fa-phone-alt"></i>
                                            {message.phone}
                                        </a>
                                    )}
                                </div>
                            </div>
                            <div className="message-status-badge">
                                <span className={getStatusBadgeClass()}>
                                    <i className={getStatusIcon()} style={{ marginRight: '5px' }}></i>
                                    {message.status}
                                </span>
                            </div>
                        </div>

                        {/* Message Meta Info */}
                        <div className="message-meta-info">
                            <div className="meta-item">
                                <i className="far fa-calendar-alt"></i>
                                <div>
                                    <span className="meta-label">Received</span>
                                    <span className="meta-value">{formatDate(message.created_at)}</span>
                                </div>
                            </div>
                            {message.subject && (
                                <div className="meta-item">
                                    <i className="fas fa-tag"></i>
                                    <div>
                                        <span className="meta-label">Subject</span>
                                        <span className="meta-value">{message.subject}</span>
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Message Content */}
                        <div className="message-content-section">
                            <h4>
                                <i className="fas fa-quote-right" style={{ marginRight: '8px', color: '#334f7f' }}></i>
                                Message
                            </h4>
                            <div className="message-text">
                                {message.message}
                            </div>
                        </div>

                        {/* Quick Actions */}
                        <div className="quick-actions">
                            {/* Reply Button - Primary Action */}
                            <button 
                                className="action-btn large primary"
                                onClick={() => setShowReplyModal(true)}
                                disabled={updating}
                            >
                                <i className="fas fa-reply"></i>
                                Reply to Message
                            </button>

                            {/* Status Management Buttons */}
                            {message.status === 'unread' && (
                                <button 
                                    className="action-btn large info"
                                    onClick={() => onMarkAsRead(message.id)}
                                    disabled={updating}
                                >
                                    <i className="fas fa-envelope-open"></i>
                                    Mark as Read
                                </button>
                            )}
                            
                            {message.status !== 'replied' && (
                                <button 
                                    className="action-btn large success"
                                    onClick={() => onMarkAsReplied(message.id)}
                                    disabled={updating}
                                >
                                    <i className="fas fa-check-circle"></i>
                                    Mark as Replied
                                </button>
                            )}
                            
                            {/* Delete Button */}
                            <button 
                                className="action-btn large danger"
                                onClick={handleDeleteClick}
                                disabled={updating}
                            >
                                <i className="fas fa-trash-alt"></i>
                                Delete Message
                            </button>
                        </div>

                        {/* Email Quick Links */}
                        <div className="email-quick-links">
                            <p className="quick-links-title">
                                <i className="fas fa-external-link-alt"></i>
                                Quick Links:
                            </p>
                            <div className="quick-links">
                                <a 
                                    href={`mailto:${message.email}?subject=Re: ${encodeURIComponent(message.subject || 'Your Inquiry')}&body=Dear ${encodeURIComponent(message.name)},%0D%0A%0D%0AThank you for contacting RedGoTrip.%0D%0A%0D%0A%0D%0A%0D%0ARegards,%0D%0ARedGoTrip Team`} 
                                    className="quick-link-btn"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                >
                                    <i className="fas fa-envelope"></i>
                                    Open in Email Client
                                </a>
                                {message.phone && (
                                    <a 
                                        href={`tel:${message.phone}`} 
                                        className="quick-link-btn"
                                    >
                                        <i className="fas fa-phone-alt"></i>
                                        Call Customer
                                    </a>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Delete Confirmation Modal */}
            {showDeleteConfirm && (
                <div className="modal-overlay" onClick={() => setShowDeleteConfirm(false)}>
                    <div className="modal-content confirm-modal" onClick={e => e.stopPropagation()}>
                        <div className="modal-header">
                            <h2>
                                <i className="fas fa-exclamation-triangle" style={{ color: '#f56565', marginRight: '10px' }}></i>
                                Confirm Delete
                            </h2>
                            <button className="modal-close" onClick={() => setShowDeleteConfirm(false)}>
                                <i className="fas fa-times"></i>
                            </button>
                        </div>
                        <div className="modal-body">
                            <div className="confirm-message">
                                <p>Are you sure you want to delete this message?</p>
                                <p className="confirm-details">
                                    <strong>From:</strong> {message.name}<br />
                                    <strong>Subject:</strong> {message.subject || 'No Subject'}
                                </p>
                                <p className="confirm-warning">This action cannot be undone.</p>
                            </div>
                            <div className="confirm-actions">
                                <button 
                                    className="btn-secondary" 
                                    onClick={() => setShowDeleteConfirm(false)}
                                >
                                    Cancel
                                </button>
                                <button 
                                    className="btn-danger" 
                                    onClick={confirmDelete}
                                    disabled={updating}
                                >
                                    {updating ? (
                                        <>
                                            <i className="fas fa-spinner fa-spin"></i>
                                            Deleting...
                                        </>
                                    ) : (
                                        <>
                                            <i className="fas fa-trash-alt"></i>
                                            Delete Permanently
                                        </>
                                    )}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Reply Modal */}
            {showReplyModal && (
                <ReplyModal
                    message={message}
                    onClose={() => setShowReplyModal(false)}
                    onSuccess={() => {
                        onMarkAsReplied(message.id);
                        setShowReplyModal(false);
                    }}
                />
            )}
        </>
    );
};

export default MessageDetailModal;